// MUI-free copy of the inline `defi-banner-bottom-wrapper` / `white-label-bottom-container` logo strip that service
// pages render under the hero (first taken from pages/ai-development-company).
import React, {memo} from 'react';
import Container from '@/components/atoms/Container';
import type {StaticImageData} from 'next/image';
import OptimizedImage from '@/components/atoms/OptimizedImage';
import type {BannerLogoItem} from '@/types';
import styles from './PlatformLogoStrip.module.scss';

type TextAlign = 'left' | 'center';

/** A banner logo with a static (`cdnImage`/import) src: the images render without width/height, as the original did. */
export type PlatformLogo = Omit<BannerLogoItem, 'src'> & {src: StaticImageData};

interface PlatformLogoStripProps {
    heading: string;
    logos: PlatformLogo[];
    /** Classes after `sub-banner-wrapper` on the band (pages use `defi-banner-bottom-wrapper`, `sub-banner-bottom-wrapper`, …). */
    wrapperClassName?: string;
    /** Grid `justifyContent`; `'start'` = no justifyContent prop (MUI default). */
    justify?: 'space-between' | 'center' | 'start';
    /**
     * - `inline` (default): heading xs 12 / sm auto / md+lg per `headingCols`; logos xs 6 / sm auto.
     * - `columns`: as inline, but logos also take md 2 / lg 1.7.
     * - `stacked`: heading xs 12 / lg 3; logos xs 6 / sm 4 / md auto.
     */
    layout?: 'inline' | 'columns' | 'stacked';
    /** Logo columns when they differ from `layout`'s (e.g. dex: inline heading, logos xs 6 / sm 4 / md auto). Defaults to `layout`. */
    logoLayout?: 'inline' | 'columns' | 'stacked';
    /** Heading column (md / lg) in the inline and columns layouts. */
    headingCols?: 'md2-lg1.7' | 'md2.5-lg2' | 'md3-lg2.5' | 'md3-lg3';
    /** Heading textAlign; `mobile` applies at ≤1024px (the pages' `mobileScreen`). */
    headingAlign?: {mobile?: TextAlign; desktop?: TextAlign};
    /** Logo images load eagerly with high fetch priority. */
    priority?: boolean;
    /** 16px left margin on each logo box (ai-development-company). */
    logoInset?: boolean;
    /** Grid item `margin: {xs: 0, sm: 0.7}` on each logo. */
    spacedLogos?: boolean;
}

const HEADING_COLS = {
    'md2-lg1.7': styles.headingMd2Lg17,
    'md2.5-lg2': styles.headingMd25Lg2,
    'md3-lg2.5': styles.headingMd3Lg25,
    'md3-lg3': styles.headingMd3Lg3
} as const;

const LOGO_LAYOUT = {
    inline: styles.logoInline,
    columns: styles.logoColumns,
    stacked: styles.logoStacked
} as const;

const JUSTIFY = {
    'space-between': styles.justifyBetween,
    center: styles.justifyCenter,
    start: undefined
} as const;

const DESKTOP_ALIGN = {left: styles.alignLeft, center: styles.alignCenter} as const;
const MOBILE_ALIGN = {left: styles.alignMobileLeft, center: styles.alignMobileCenter} as const;

const cx = (...classes: (string | false | undefined)[]) => classes.filter(Boolean).join(' ');

const PlatformLogoStrip = ({
    heading,
    logos,
    wrapperClassName = 'defi-banner-bottom-wrapper',
    justify = 'space-between',
    layout = 'inline',
    logoLayout = layout,
    headingCols = 'md2-lg1.7',
    headingAlign,
    priority = false,
    logoInset = false,
    spacedLogos = false
}: PlatformLogoStripProps) => {
    const headingItemClass =
        layout === 'stacked' ? cx(styles.item, styles.headingStacked) : cx(styles.item, styles.headingInline, HEADING_COLS[headingCols]);
    const headingClass = cx(
        'banner-bottom-heading',
        styles.heading,
        headingAlign?.desktop && DESKTOP_ALIGN[headingAlign.desktop],
        headingAlign?.mobile && MOBILE_ALIGN[headingAlign.mobile]
    );
    const logoItemClass = cx(styles.item, LOGO_LAYOUT[logoLayout], spacedLogos && styles.logoSpaced, 'sub-banner-images');
    const logoBoxClass = cx('images-banner-bottom', logoInset && styles.logoInset);

    return (
        <div className={cx('sub-banner-wrapper', wrapperClassName)}>
            <Container>
                <div className={cx(styles.grid, JUSTIFY[justify], 'white-label-bottom-container')}>
                    <div className={headingItemClass}>
                        <p className={headingClass}>{heading}</p>
                    </div>
                    {logos.map((logo, index) => (
                        <div className={logoItemClass} key={index + 1}>
                            <div className={logoBoxClass}>
                                <OptimizedImage src={logo.src} alt={logo.alt} priority={priority} />
                            </div>
                        </div>
                    ))}
                </div>
            </Container>
        </div>
    );
};

export default memo(PlatformLogoStrip);
